import React, { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { AlertTriangle, Clock, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASON_OPTIONS = [
  { code: "not_on_site", label: "Wasn't on site that day" },
  { code: "didnt_see", label: "Didn't see it / missed the prompt" },
  { code: "signal_issue", label: "Phone or signal issue" },
  { code: "off_sick", label: "Off sick / on leave" },
  { code: "other", label: "Other (please specify)" },
];

const fmtDate = (d) => {
  try {
    const [y, m, day] = d.split("-").map(Number);
    const dt = new Date(y, m - 1, day);
    return dt.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
  } catch {
    return d;
  }
};

/**
 * Pops up on app load (after login) if the current user has missed daily
 * toolbox sign-offs from prior days. They must select a reason code and
 * (for "other") provide a quick note before they can dismiss each item.
 */
export const MissedSignoffsPromptModal = ({ user }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [reasonCode, setReasonCode] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const run = async () => {
      try {
        const res = await axios.get(`${API}/me/missed-signoffs`);
        if (cancelled) return;
        const rows = res.data?.items || [];
        if (rows.length > 0) {
          setItems(rows);
          setIdx(0);
          setReasonCode("");
          setReasonText("");
          setOpen(true);
        }
      } catch (e) {
        // silent — non-blocking
      }
    };
    // small delay so the rest of the app finishes loading first
    const t = setTimeout(run, 1200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [user?.id]);

  const current = items[idx];

  const advance = () => {
    setReasonCode("");
    setReasonText("");
    if (idx + 1 >= items.length) {
      setOpen(false);
    } else {
      setIdx(idx + 1);
    }
  };

  const submit = async () => {
    if (!current) return;
    if (!reasonCode) {
      toast.error("Pick a reason mate");
      return;
    }
    const reasonLabel = REASON_OPTIONS.find((r) => r.code === reasonCode)?.label || reasonCode;
    const finalReason =
      reasonCode === "other"
        ? reasonText.trim()
        : (reasonText.trim() ? `${reasonLabel} — ${reasonText.trim()}` : reasonLabel);
    if (reasonCode === "other" && finalReason.length < 3) {
      toast.error("Pop in a quick note so we know what happened");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/me/missed-signoffs/reason`, {
        allocation_id: current.allocation_id,
        date: current.date,
        reason: finalReason,
        reason_code: reasonCode,
      });
      toast.success("Cheers — logged");
      advance();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't record the reason");
    } finally {
      setSubmitting(false);
    }
  };

  const signNow = () => {
    if (!current) return;
    // Send them to the Forms page to acknowledge — keep modal closed for now
    setOpen(false);
    window.location.href = `/forms?tab=acknowledgements`;
  };

  if (!open || !current) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !submitting) setOpen(false); }}>
      <DialogContent
        data-testid="missed-signoffs-modal"
        className="max-w-lg"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Toolbox sign-off missed
          </DialogTitle>
          <DialogDescription>
            We've noticed a daily sign-off you didn't get to. Could you let us know why?
            {items.length > 1 && (
              <span className="block mt-1 text-xs font-semibold">
                {idx + 1} of {items.length}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
            <ClipboardCheck className="w-4 h-4" />
            {current.template_name || "Toolbox Talk"}
          </div>
          <div className="text-sm text-slate-700">
            <span className="font-semibold">Job:</span> {current.job_title || "—"}
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-700">
            <Clock className="w-4 h-4" />
            <span className="font-semibold">Date:</span> {fmtDate(current.date)}
            {current.primary_submitted_by && (
              <span className="text-xs text-slate-500">· filled by {current.primary_submitted_by}</span>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-semibold">Why didn't you sign?</Label>
          <div className="space-y-1.5" data-testid="missed-signoffs-reason-options">
            {REASON_OPTIONS.map((opt) => (
              <label
                key={opt.code}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition ${
                  reasonCode === opt.code
                    ? "bg-orange-50 border-orange-400"
                    : "bg-white border-slate-200 hover:border-slate-400"
                }`}
              >
                <input
                  type="radio"
                  name="miss-reason"
                  value={opt.code}
                  checked={reasonCode === opt.code}
                  onChange={() => setReasonCode(opt.code)}
                  data-testid={`miss-reason-${opt.code}`}
                />
                <span className="text-sm">{opt.label}</span>
              </label>
            ))}
          </div>
          {(reasonCode === "other" || reasonCode === "off_sick") && (
            <Textarea
              data-testid="missed-signoffs-reason-text"
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              placeholder={reasonCode === "other" ? "Tell us what happened..." : "Optional extra detail"}
              rows={3}
              className="mt-2"
            />
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-between pt-2">
          <Button
            variant="outline"
            onClick={signNow}
            data-testid="missed-signoffs-go-sign-btn"
            disabled={submitting}
          >
            Sign it now instead
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !reasonCode}
            data-testid="missed-signoffs-submit-btn"
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            {submitting ? "Saving..." : "Submit reason"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MissedSignoffsPromptModal;
